import React from 'react';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { isHttpUrl } from '@/utils/paymentUrl';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { clientPaths } from '@/utils/clientPaths';

export interface UnpaidPaymentItem {
  id?: string | number;
  eventName: string;
  dateStr: string;
  venueName?: string;
  amount: number;
  paymentUrl?: string;
  orderNumber?: string;
}

interface UnpaidPaymentCardProps {
  items?: UnpaidPaymentItem[];
  onDismiss?: () => void;
}

const DISMISSED_KEY = 'sporto_dismissed_unpaid_payments';

const getItemKey = (item: UnpaidPaymentItem): string => {
  if (item.orderNumber) return String(item.orderNumber);
  if (item.id) return String(item.id);
  return `${item.eventName}_${item.dateStr}_${item.amount}`;
};

const getDismissedKeys = (): string[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const addDismissedKey = (key: string) => {
  try {
    const existing = getDismissedKeys();
    if (!existing.includes(key)) {
      const updated = [...existing, key];
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
    }
  } catch (err) {
    console.error('Failed to save dismissed unpaid payment key:', err);
  }
};

export const UnpaidPaymentCard: React.FC<UnpaidPaymentCardProps> = ({ items = [], onDismiss }) => {
  const navigate = useNavigate();
  const [dismissedKeys, setDismissedKeys] = React.useState<string[]>(() => getDismissedKeys());

  // Find first item that hasn't been dismissed by user
  const item = React.useMemo(() => {
    if (!items || items.length === 0) return null;
    return items.find((it) => !dismissedKeys.includes(getItemKey(it))) || null;
  }, [items, dismissedKeys]);

  const isPast = React.useMemo(() => {
    if (!item?.dateStr) return false;
    const d = new Date(item.dateStr);
    if (isNaN(d.getTime())) return false;
    return d < new Date();
  }, [item?.dateStr]);

  if (!item) {
    return null;
  }

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    })
      .format(val)
      .replace('IDR', 'Rp.');
  };

  const formatIndonesianDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return dateStr;
    }
    return d.toLocaleDateString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-3xl p-4 shadow-[inset_0_0_0_1px_#E0E6ED] flex flex-col gap-3.5 relative">
      {/* Header Info */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl bg-[#FFF4EE] text-sporto-orange flex items-center justify-center flex-shrink-0">
            <Icon icon="ph:credit-card" className="w-6 h-6" />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <h3 className="text-base font-bold text-gray-900 leading-snug">
              Pembayaran belum selesai
            </h3>
            <p className="text-xs text-gray-500">
              {isPast
                ? 'Event sudah berakhir, tagihanmu masih terbuka'
                : 'Segera lunasi tagihanmu untuk mengamankan slot event'}
            </p>
          </div>
        </div>

        {/* Dismiss Button */}
        <button
          type="button"
          onClick={() => {
            const currentKey = getItemKey(item);
            addDismissedKey(currentKey);
            setDismissedKeys((prev) => [...prev, currentKey]);
            if (onDismiss) onDismiss();
          }}
          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer shrink-0 -mr-1 -mt-1"
          aria-label="Tutup"
        >
          <Icon icon="ph:x-bold" className="w-4 h-4" />
        </button>
      </div>

      {/* Item Card Details */}
      <div className="bg-gray-50 rounded-xl p-3 px-4 flex items-center justify-between gap-3 shadow-[inset_0_0_0_1px_#E0E6ED]">
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {item.eventName}
          </span>
          <span className="text-xs text-gray-500 truncate">
            {formatIndonesianDate(item.dateStr)} {item.venueName ? `· ${item.venueName}` : ''}
          </span>
        </div>
        <span className="text-base font-semibold text-[#FC480D] flex-shrink-0">
          {formatCurrency(item.amount)}
        </span>
      </div>

      {/* Action Button */}
      <button
        type="button"
        onClick={() => {
          if (isHttpUrl(item.paymentUrl)) {
            void openExternalUrl(item.paymentUrl);
          } else if (item.orderNumber) {
            navigate(clientPaths.event.checkout(item.orderNumber), {
              state: {
                orderNumber: item.orderNumber,
                eventName: item.eventName,
                amount: item.amount,
              },
            });
          } else {
            navigate('/transactions');
          }
        }}
        className="h-11 rounded-full bg-[#FC480D] text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-sm hover:bg-[#D83B08] active:scale-98 transition-all w-full cursor-pointer"
      >
        <Icon icon="ph:qr-code" className="w-4 h-4" />
        <span>Bayar Sekarang</span>
      </button>
    </div>
  );
};

export default UnpaidPaymentCard;
